import { FC } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Props for `CoreValuesGrid`.
 */
export type CoreValuesGridProps =
  SliceComponentProps<Content.CoreValuesGridSlice>;

/**
 * Component for "CoreValuesGrid" Slices.
 */
const CoreValuesGrid: FC<CoreValuesGridProps> = ({ slice }) => {
  const { heading, subheading, values } = slice.primary;

  // Check if this is the 8CardsSupportRvBg variation
  const is8CardsSupportRvBg = slice.variation === '8CardsSupportRvBg';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={is8CardsSupportRvBg ? '' : 'bg-[#0F423A]'}
      style={
        is8CardsSupportRvBg
          ? {
              background:
                'linear-gradient(180deg, #0F3E37 9.26%, #0E1110 94.74%)',
            }
          : undefined
      }
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Main container with glassmorphism effect */}
        <div className="rounded-2xl p-8 md:p-12">
          {/* Header Section */}
          <div className="text-center mb-16">
            {heading && (
              <div className="mb-6">
                <PrismicRichText
                  field={heading}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal text-white leading-tight font-inter">
                        {children}
                      </h2>
                    ),
                  }}
                />
              </div>
            )}
            {subheading && (
              <div className="max-w-2xl mx-auto">
                <PrismicRichText
                  field={subheading}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[18px] text-white leading-relaxed font-inter">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* Values Grid */}
          {values && values.length > 0 && (
            <div
              className={
                is8CardsSupportRvBg
                  ? 'flex flex-wrap justify-center gap-8'
                  : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
              }
            >
              {values.map((value, index) => (
                <div
                  key={index}
                  className={`rounded-2xl p-6 ${
                    is8CardsSupportRvBg
                      ? 'w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-sm'
                      : 'w-full max-w-sm'
                  }`}
                  style={{
                    background: is8CardsSupportRvBg
                      ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.20) 100%)'
                      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)',
                    boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {/* Icon */}
                  {value.icon && (
                    <div className="mb-6">
                      <PrismicNextImage
                        field={value.icon}
                        className="w-12 h-12 object-contain"
                        fallbackAlt=""
                      />
                    </div>
                  )}

                  {/* Title */}
                  {value.title && (
                    <div className="mb-4">
                      <PrismicRichText
                        field={value.title}
                        components={{
                          heading4: ({ children }) => (
                            <h4 className="text-xl font-semibold text-white leading-tight">
                              {children}
                            </h4>
                          ),
                        }}
                      />
                    </div>
                  )}

                  {/* Description */}
                  {value.description && (
                    <div>
                      <PrismicRichText
                        field={value.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-white/70 leading-relaxed">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CoreValuesGrid;
